import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { categoryConfigs, getColorClass, getStatusBadge, Project, ProjectCategory } from "@/mocks/projectsData";
import { getSellingTypeColorClass, getSellingTypeBgClass, SellingType, defaultSellingTypes } from "@/mocks/sellingTypesData";
import { InventoryField, defaultInventoryFields } from "@/mocks/inventoryFieldsData";
import { fetchApi } from "@/services/apiService";
import { useToast } from "@/components/base/Toast";

const statusOptions = ["Active", "Upcoming", "Draft", "Inactive"];
const projectColorOptions = ["indigo", "green", "amber", "purple", "blue", "pink", "teal", "red"];

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [projects, setProjects] = useState<any[]>([]);
  const [sellingTypes, setSellingTypes] = useState<SellingType[]>([]);
  const [inventoryFields, setInventoryFields] = useState<InventoryField[]>([]);
  const [leadsList, setLeadsList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sellingTypeFilter, setSellingTypeFilter] = useState("all");

  // Create / Edit Modal State
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);

  // Form State
  const [projectName, setProjectName] = useState("");
  const [selectedSellingTypeId, setSelectedSellingTypeId] = useState<string>("");
  const [projectStatus, setProjectStatus] = useState("Active");
  const [projectColor, setProjectColor] = useState("indigo");
  const [projectDesc, setProjectDesc] = useState("");
  const [projectClientName, setProjectClientName] = useState("");
  const [projectBudget, setProjectBudget] = useState<string>("");
  const [projectStartDate, setProjectStartDate] = useState("");
  const [projectDueDate, setProjectDueDate] = useState("");
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [firstItemValues, setFirstItemValues] = useState<Record<string, string>>({ status: "Available" });

  // Action Menu State
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const [projRes, typesRes, fieldsRes, leadsRes] = await Promise.all([
        fetchApi<any[]>('/projects', undefined, []),
        fetchApi<SellingType[]>('/projects/settings/selling-types', undefined, []),
        fetchApi<InventoryField[]>('/projects/settings/inventory-fields', undefined, []),
        fetchApi<any>('/leads?limit=200', undefined, []),
      ]);

      setProjects(Array.isArray(projRes) ? projRes : (projRes as any)?.data || []);
      const rawTypes = Array.isArray(typesRes) ? typesRes : (typesRes as any)?.data || [];
      setSellingTypes(rawTypes.length > 0 ? rawTypes : defaultSellingTypes);

      const rawFields = Array.isArray(fieldsRes) ? fieldsRes : (fieldsRes as any)?.data || [];
      setInventoryFields(rawFields.length > 0 ? rawFields : defaultInventoryFields);
      
      const rawLeads = Array.isArray(leadsRes)
        ? leadsRes
        : Array.isArray(leadsRes?.items)
        ? leadsRes.items
        : Array.isArray(leadsRes?.data?.items)
        ? leadsRes.data.items
        : Array.isArray(leadsRes?.data)
        ? leadsRes.data
        : [];
      setLeadsList(rawLeads);
    } catch (err: any) {
      console.error("Failed to load projects:", err);
      showToast(err.message || "Failed to load projects", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const allTypes = sellingTypes.length > 0 ? sellingTypes : defaultSellingTypes;
  const allFields = inventoryFields.length > 0 ? inventoryFields : defaultInventoryFields;

  const selectedType = allTypes.find((s) => s.id === selectedSellingTypeId) || allTypes[0];

  const formCustomFields = useMemo(() => {
    if (!selectedType) return allFields.filter((f) => f.active && f.showInForm);
    const matched = allFields.filter(
      (f) =>
        selectedType.inventoryFieldIds?.some(
          (fid) =>
            String(fid).toLowerCase() === String(f.id).toLowerCase() ||
            f.label.toLowerCase() === String(fid).toLowerCase() ||
            (f.fieldKey && f.fieldKey.toLowerCase() === String(fid).toLowerCase())
        ) &&
        f.active &&
        f.showInForm
    );
    return matched.length > 0 ? matched : allFields.filter((f) => f.active && f.showInForm);
  }, [selectedType, allFields]);

  const getSellingTypeFieldNames = (st: SellingType) => {
    if (!st.inventoryFieldIds || !Array.isArray(st.inventoryFieldIds) || st.inventoryFieldIds.length === 0) {
      return "Unit Number, Type, Floor, Area (sqft), Price, Status, Buyer Name";
    }
    const matched = allFields.filter((f) =>
      st.inventoryFieldIds.some((fid) => String(fid) === String(f.id) || f.label === fid || f.fieldKey === fid)
    );
    if (matched.length > 0) {
      return matched.map((f) => f.label).join(", ");
    }
    return "Unit Number, Area (sqft), Price, Status";
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditingProjectId(null);
    setProjectName("");
    setSelectedSellingTypeId(sellingTypes[0]?.id || "");
    setProjectStatus("Active");
    setProjectColor("indigo");
    setProjectDesc("");
    setProjectClientName("");
    setProjectBudget("");
    setProjectStartDate("");
    setProjectDueDate("");
    setCustomFieldValues({});
    setFirstItemValues({ status: "Available" });
  };

  const openCreateModal = () => {
    resetForm();
    if (sellingTypes.length > 0) {
      setSelectedSellingTypeId(sellingTypes[0].id);
    }
    setShowModal(true);
  };

  const openEditModal = (project: any) => {
    setIsEditing(true);
    setEditingProjectId(project.id);
    setProjectName(project.name || "");
    setSelectedSellingTypeId(project.sellingTypeId || project.selling_type_id || "");
    setProjectStatus(project.status || "Active");
    setProjectColor(project.color || "indigo");
    setProjectDesc(project.description || "");
    setProjectClientName(project.client_name || "");
    setProjectBudget(project.budget ? String(project.budget) : "");
    setProjectStartDate(project.start_date || "");
    setProjectDueDate(project.due_date || "");
    setCustomFieldValues(project.customFields || project.custom_fields || {});
    setShowModal(true);
    setOpenActionMenuId(null);
  };

  const handleSaveProject = async () => {
    if (!projectName.trim()) {
      showToast("Please enter a project name", "error");
      return;
    }

    setIsSubmitting(true);
    const cat: ProjectCategory = selectedType?.name.toLowerCase().includes("product")
      ? "products"
      : selectedType?.name.toLowerCase().includes("course")
      ? "courses"
      : selectedType?.name.toLowerCase().includes("plan")
      ? "plans"
      : selectedType?.name.toLowerCase().includes("package")
      ? "packages"
      : selectedType?.name.toLowerCase().includes("custom")
      ? "custom"
      : "properties";

    const payload = {
      name: projectName.trim(),
      category: cat,
      sellingTypeId: selectedSellingTypeId || null,
      status: projectStatus,
      color: projectColor,
      description: projectDesc,
      client_name: projectClientName.trim() || null,
      budget: parseFloat(projectBudget) || 0,
      start_date: projectStartDate || null,
      due_date: projectDueDate || null,
      customFields: customFieldValues,
    };

    try {
      if (isEditing && editingProjectId) {
        const updated = await fetchApi<any>(`/projects/${editingProjectId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setProjects((prev) =>
          prev.map((p) => (p.id === editingProjectId ? { ...p, ...payload, ...updated } : p))
        );
        showToast("Project updated successfully!", "success");
      } else {
        const created = await fetchApi<any>('/projects', {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        // Add first item if provided
        const hasFirstItem = Object.values(firstItemValues).some((v) => v && v.trim() && v !== "Available");
        if (created?.id && hasFirstItem) {
          await fetchApi(`/projects/${created.id}/inventory`, {
            method: 'POST',
            body: JSON.stringify(firstItemValues),
          }).catch(() => {});
        }

        setProjects((prev) => [created, ...prev]);
        showToast("Project created successfully!", "success");
      }
      setShowModal(false);
      resetForm();
    } catch (err: any) {
      showToast(err.message || "Failed to save project", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async (id: string) => {
    try {
      await fetchApi(`/projects/${id}`, { method: 'DELETE' });
      setProjects((prev) => prev.filter((p) => p.id !== id));
      showToast("Project deleted successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to delete project", "error");
    } finally {
      setDeleteConfirmId(null);
      setOpenActionMenuId(null);
    }
  };

  const getCategorySummary = (project: any) => {
    const fields = project.customFields || project.custom_fields || {};
    const cat = project.category || "properties";
    if (cat === "properties") {
      const loc = fields.location || "";
      const pFrom = fields.priceFrom || "";
      const pTo = fields.priceTo || "";
      const parts = [loc, pFrom && pTo ? `${pFrom} — ${pTo}` : pFrom || pTo].filter(Boolean);
      return parts.length ? parts.join(" • ") : project.description || "Residential development";
    }
    if (cat === "products") {
      const pCat = fields.productCategory || "";
      const pFrom = fields.priceFrom || "";
      const pTo = fields.priceTo || "";
      return [pCat, pFrom && pTo ? `${pFrom} — ${pTo}` : pFrom || pTo].filter(Boolean).join(" • ");
    }
    if (cat === "courses") {
      const fee = fields.fee || "";
      const mode = fields.mode || "Online";
      const seats = fields.totalSeats ? `${fields.totalSeats} seats` : "";
      return [mode, seats, fee].filter(Boolean).join(" • ");
    }
    if (cat === "plans") {
      const mo = fields.priceMonth ? `${fields.priceMonth}/mo` : "";
      const users = fields.maxUsers ? `Up to ${fields.maxUsers} users` : "";
      return [mo, users].filter(Boolean).join(" • ");
    }
    if (cat === "packages") {
      const dur = fields.duration || "";
      const price = fields.priceFrom ? `From ${fields.priceFrom}` : "";
      return [dur, price].filter(Boolean).join(" • ");
    }
    return project.description || "Custom business line";
  };

  const getInventorySummary = (project: any) => {
    const total = project.totalInventory || project.leads || 0;
    const avail = project.availableCount !== undefined ? project.availableCount : Math.max(0, total - (project.booked || 0));
    const booked = project.booked || 0;
    const st = sellingTypes.find((s) => s.id === (project.sellingTypeId || project.selling_type_id));
    const itemLabel = st?.itemLabel || "Units";
    const soldLabel = st?.soldStatus || "Sold";
    return `${total} ${itemLabel} • ${avail} Available • ${booked} ${soldLabel}`;
  };

  // Metrics
  const metrics = useMemo(() => {
    const totalProjects = projects.length;
    const activeProjects = projects.filter((p) => p.status === "Active").length;
    const totalLeads = projects.reduce((acc, p) => acc + (Number(p.leads) || 0), 0);
    const totalBooked = projects.reduce((acc, p) => acc + (Number(p.booked) || 0), 0);
    const totalBudget = projects.reduce((acc, p) => acc + (Number(p.budget) || 0), 0);
    const avgConversion = totalLeads > 0 ? ((totalBooked / totalLeads) * 100).toFixed(1) : "0.0";
    return { totalProjects, activeProjects, totalLeads, totalBooked, totalBudget, avgConversion };
  }, [projects]);

  // Filtered List
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      if (statusFilter !== "All" && p.status !== statusFilter) return false;
      if (sellingTypeFilter !== "all" && p.sellingTypeId !== sellingTypeFilter && p.selling_type_id !== sellingTypeFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const nameMatch = p.name?.toLowerCase().includes(q);
        const descMatch = p.description?.toLowerCase().includes(q);
        const clientMatch = p.client_name?.toLowerCase().includes(q);
        const catMatch = p.category?.toLowerCase().includes(q);
        if (!nameMatch && !descMatch && !clientMatch && !catMatch) return false;
      }
      return true;
    });
  }, [projects, statusFilter, sellingTypeFilter, searchQuery]);

  return (
    <CrmLayout title="Projects">
      <div className="p-4 md:p-6 space-y-6 max-w-[1600px] mx-auto">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage your properties, products, courses, and services across all industries
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/settings/projects")}
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold shadow-sm transition-all cursor-pointer"
            >
              <i className="ri-settings-3-line text-sm text-gray-500" />
              Configure Types
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-sm hover:shadow transition-all cursor-pointer"
            >
              <i className="ri-add-line text-base" />
              New Project
            </button>
          </div>
        </div>

        {/* Dynamic Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4.5 shadow-sm">
            <div className="flex items-center gap-2.5 text-gray-500 text-xs font-medium mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <i className="ri-building-line text-sm" />
              </div>
              Active Projects
            </div>
            <p className="text-2xl font-bold text-gray-900">{metrics.activeProjects}</p>
            <p className="text-xs text-gray-400 mt-0.5">of {metrics.totalProjects} total registered</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4.5 shadow-sm">
            <div className="flex items-center gap-2.5 text-gray-500 text-xs font-medium mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <i className="ri-user-shared-line text-sm" />
              </div>
              Total Leads Across Projects
            </div>
            <p className="text-2xl font-bold text-gray-900">{metrics.totalLeads}</p>
            <p className="text-xs text-green-600 mt-0.5 font-medium">{metrics.totalBooked} deals closed / sold</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4.5 shadow-sm">
            <div className="flex items-center gap-2.5 text-gray-500 text-xs font-medium mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                <i className="ri-pie-chart-line text-sm" />
              </div>
              Selling Types Active
            </div>
            <p className="text-2xl font-bold text-gray-900">{sellingTypes.length}</p>
            <p className="text-xs text-gray-400 mt-0.5">Configured schemas</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4.5 shadow-sm">
            <div className="flex items-center gap-2.5 text-gray-500 text-xs font-medium mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <i className="ri-line-chart-line text-sm" />
              </div>
              Avg Conversion
            </div>
            <p className="text-2xl font-bold text-gray-900">{metrics.avgConversion}%</p>
            <p className="text-xs text-indigo-600 mt-0.5 font-medium">
              {metrics.totalBudget > 0 ? `₹${metrics.totalBudget.toLocaleString('en-IN')} pipeline` : "Real-time track"}
            </p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center justify-between gap-3 shadow-sm">
          <div className="flex items-center gap-2.5 flex-1 min-w-[240px] max-w-md">
            <div className="relative w-full">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search projects, client, category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Status Filter */}
            <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg text-xs font-medium">
              {["All", "Active", "Upcoming", "Draft"].map((st) => (
                <button
                  key={st}
                  onClick={() => setStatusFilter(st)}
                  className={`px-2.5 py-1 rounded-md transition-colors cursor-pointer ${
                    statusFilter === st ? "bg-white text-gray-900 shadow-xs font-semibold" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>

            {/* Selling Type Filter Dropdown */}
            {sellingTypes.length > 0 && (
              <select
                value={sellingTypeFilter}
                onChange={(e) => setSellingTypeFilter(e.target.value)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer shadow-xs"
              >
                <option value="all">All Selling Types</option>
                {sellingTypes.map((st) => (
                  <option key={st.id} value={st.id}>{st.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center shadow-sm">
            <div className="inline-block w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-xs text-gray-500">Loading projects &amp; live schemas...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm max-w-lg mx-auto">
            <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-4 text-2xl">
              <i className="ri-building-line" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">No Projects Found</h3>
            <p className="text-xs text-gray-500 mb-5">
              {searchQuery || statusFilter !== "All" || sellingTypeFilter !== "all"
                ? "No projects match your active filter criteria."
                : "Create your first project to organize properties, inventory, and linked leads."}
            </p>
            <button
              onClick={openCreateModal}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4.5 py-2 text-sm font-medium transition-colors shadow-sm cursor-pointer"
            >
              + Create First Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4.5">
            {filteredProjects.map((project) => {
              const st = project.sellingTypeId ? sellingTypes.find((s) => s.id === project.sellingTypeId) : null;
              const cfg = categoryConfigs[project.category as ProjectCategory] || categoryConfigs.properties;

              return (
                <div
                  key={project.id}
                  className="bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all overflow-hidden flex flex-col relative group"
                >
                  {/* Top Color Banner */}
                  <div className={`h-1.5 w-full ${getColorClass(project.color)}`} />

                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      {/* Card Top Row */}
                      <div className="flex items-start justify-between gap-3 mb-2.5">
                        <div className="min-w-0 flex-1">
                          <h3
                            onClick={() => navigate(`/projects/${project.id}`)}
                            className="text-lg font-bold text-gray-900 hover:text-indigo-600 transition-colors cursor-pointer truncate"
                          >
                            {project.name}
                          </h3>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full flex items-center gap-1">
                              <i className={`${cfg.icon} text-[11px]`} />
                              {cfg.title}
                            </span>
                            {st && (
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1.5 ${getSellingTypeBgClass(st.color)}`}>
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getSellingTypeColorClass(st.color)}`} />
                                {st.name}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(project.status, project.category || 'properties')}`}>
                              {project.status}
                            </span>
                          </div>
                        </div>

                        {/* Action Menu Button */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenuId(openActionMenuId === project.id ? null : project.id);
                            }}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-more-2-fill text-base" />
                          </button>

                          {openActionMenuId === project.id && (
                            <>
                              <div className="fixed inset-0 z-20" onClick={() => setOpenActionMenuId(null)} />
                              <div className="absolute right-0 top-full mt-1 z-30 bg-white rounded-xl border border-gray-200 shadow-xl py-1.5 w-44">
                                <button
                                  onClick={() => navigate(`/projects/${project.id}`)}
                                  className="w-full text-left px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer"
                                >
                                  <i className="ri-eye-line text-sm text-gray-500" />
                                  View Details
                                </button>
                                <button
                                  onClick={() => openEditModal(project)}
                                  className="w-full text-left px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer"
                                >
                                  <i className="ri-edit-line text-sm text-gray-500" />
                                  Edit Project
                                </button>
                                <div className="border-t border-gray-100 my-1" />
                                <button
                                  onClick={() => {
                                    setDeleteConfirmId(project.id);
                                    setOpenActionMenuId(null);
                                  }}
                                  className="w-full text-left px-3.5 py-2 text-xs font-medium text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line text-sm text-red-500" />
                                  Delete Project
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Summary Lines */}
                      <div className="space-y-1 my-3">
                        <p className="text-sm font-medium text-gray-700">{getCategorySummary(project)}</p>
                        <p className="text-xs text-gray-500">{getInventorySummary(project)}</p>
                        {project.client_name && (
                          <p className="text-xs text-indigo-600 font-medium">Client: {project.client_name}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      {/* Metric Badges */}
                      <div className="flex flex-wrap items-center gap-2 mb-3.5 pt-2 border-t border-gray-100">
                        <span className="px-2.5 py-0.5 text-xs font-semibold bg-indigo-50 text-indigo-700 rounded-full">
                          {project.leads || 0} leads
                        </span>
                        <span className="px-2.5 py-0.5 text-xs font-semibold bg-green-50 text-green-700 rounded-full">
                          {project.interested || 0} interested
                        </span>
                        <span className="px-2.5 py-0.5 text-xs font-semibold bg-purple-50 text-purple-700 rounded-full">
                          {project.booked || 0} {st?.soldStatus?.toLowerCase() || "booked"}
                        </span>
                        {project.budget > 0 && (
                          <span className="px-2.5 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 rounded-full ml-auto">
                            ₹{project.budget.toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-500 font-medium">{st?.progressLabel || "Sales progress"}</span>
                          <span className="font-bold text-gray-800">{project.progress || 0}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${getColorClass(project.color)}`}
                            style={{ width: `${Math.min(100, project.progress || 0)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create / Edit Project Modal Matching User Design (Screenshots 1, 2, 3) */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                <h2 className="text-base font-bold text-gray-900">
                  {isEditing ? "Edit Project" : "Create New Project"}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="p-6 space-y-5 flex-1">
                {/* 1. WHAT ARE YOU SELLING? Selectable Cards Section (Create Mode Only) */}
                {!isEditing && (
                  <div className="space-y-2.5">
                    <div>
                      <p className="text-[11px] font-bold text-gray-800 uppercase tracking-wider">
                        WHAT ARE YOU SELLING?
                      </p>
                      <p className="text-xs text-gray-500">
                        Select from your configured selling types
                      </p>
                    </div>

                    {/* Selling Type Cards */}
                    <div className="space-y-2">
                      {sellingTypes.map((st) => {
                        const isSelected = selectedSellingTypeId === st.id;
                        return (
                          <div
                            key={st.id}
                            onClick={() => setSelectedSellingTypeId(st.id)}
                            className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                              isSelected
                                ? "border-indigo-600 bg-indigo-50/20 shadow-xs ring-1 ring-indigo-500"
                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getSellingTypeColorClass(st.color)}`} />
                                <h4 className="text-sm font-semibold text-gray-900 truncate">{st.name}</h4>
                              </div>
                              <p className="text-xs text-gray-400 truncate mt-0.5 ml-4.5">
                                {getSellingTypeFieldNames(st)}
                              </p>
                            </div>

                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 text-xs shadow-xs">
                                <i className="ri-check-line" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Blue Notification Banner */}
                    {selectedType && (
                      <div className="p-3 bg-indigo-50/60 rounded-xl border border-indigo-100 text-xs text-indigo-700 flex items-center gap-2">
                        <i className="ri-check-line text-indigo-600 text-sm font-bold" />
                        <span>
                          Inventory tab will show: <strong>{getSellingTypeFieldNames(selectedType)}</strong>
                        </span>
                      </div>
                    )}

                    {/* 2. Add First Item (Optional) Section */}
                    <div className="pt-2 border-t border-gray-100 space-y-2.5">
                      <div>
                        <h4 className="text-xs font-bold text-gray-900">Add First Item (Optional)</h4>
                        <p className="text-xs text-gray-400">You can add more items after creating the project</p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Unit Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. A-101"
                            value={firstItemValues.unitNo || ""}
                            onChange={(e) => setFirstItemValues({ ...firstItemValues, unitNo: e.target.value })}
                            className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Price <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="₹ e.g. 1.8 Cr"
                            value={firstItemValues.price || ""}
                            onChange={(e) => setFirstItemValues({ ...firstItemValues, price: e.target.value })}
                            className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Status <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={firstItemValues.status || "Available"}
                            onChange={(e) => setFirstItemValues({ ...firstItemValues, status: e.target.value })}
                            className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white text-gray-800"
                          >
                            <option value="Available">Available</option>
                            <option value="Booked">Booked</option>
                            <option value="Sold">Sold</option>
                            <option value="On Hold">On Hold</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Area (sqft)
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. 950"
                            value={firstItemValues.area || ""}
                            onChange={(e) => setFirstItemValues({ ...firstItemValues, area: e.target.value })}
                            className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 3. Project Details Section */}
                <div className="space-y-3 pt-2 border-t border-gray-100">
                  {/* Project Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Bandra West Complex"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white"
                    />
                  </div>

                  {/* Status & Color */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={projectStatus}
                        onChange={(e) => setProjectStatus(e.target.value)}
                        className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white text-gray-800"
                      >
                        {statusOptions.map((st) => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Project Color</label>
                      <div className="flex items-center gap-2 flex-wrap pt-0.5">
                        {projectColorOptions.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setProjectColor(c)}
                            className={`w-6 h-6 rounded-full ${getColorClass(c)} transition-transform cursor-pointer ${
                              projectColor === c ? "ring-2 ring-offset-2 ring-indigo-600 scale-110" : "hover:scale-105"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Client Name & Budget (Optional fields in Edit / Advanced) */}
                  {isEditing && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Client / Developer Name</label>
                        <input
                          type="text"
                          placeholder="e.g. Apex Developers"
                          value={projectClientName}
                          onChange={(e) => setProjectClientName(e.target.value)}
                          className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Budget (₹)</label>
                        <input
                          type="number"
                          placeholder="e.g. 5000000"
                          value={projectBudget}
                          onChange={(e) => setProjectBudget(e.target.value)}
                          className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      rows={3}
                      placeholder="Describe the project..."
                      value={projectDesc}
                      onChange={(e) => setProjectDesc(e.target.value)}
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 resize-none bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-2.5 px-6 py-3.5 border-t border-gray-100 sticky bottom-0 bg-white z-10">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveProject}
                  disabled={isSubmitting}
                  className="px-5 py-2 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {isSubmitting ? <i className="ri-loader-4-line animate-spin" /> : null}
                  {isEditing ? "Save Changes" : "Create Project"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
              <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto text-2xl">
                <i className="ri-error-warning-line" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-900">Delete Project?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to delete this project? This will remove its inventory, tasks, and unbind attached leads.
                </p>
              </div>
              <div className="flex items-center justify-center gap-3 pt-2">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteProject(deleteConfirmId)}
                  className="px-4.5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors cursor-pointer shadow-sm"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}